import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import { supabase } from '../../lib/supabase';
import RegionSelector from './RegionSelector';
import { useLocale } from '../../context/LocaleContext';

// ─── Rotating intel feed that fades between lines ───
const IntelFeed = ({ lines, interval = 5000 }) => {
  const [index, setIndex] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    if (!lines.length) return;
    const timer = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setIndex((prev) => (prev + 1) % lines.length);
        setFade(true);
      }, 350);
    }, interval);
    return () => clearInterval(timer);
  }, [lines.length, interval]);

  if (!lines.length) return null;

  return (
    <span className={`transition-opacity duration-350 ${fade ? 'opacity-100' : 'opacity-0'}`}>
      {lines[index]}
    </span>
  );
};

const Header = () => {
  const location = useLocation();
  const { politicalEntityLabel, language } = useLocale();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [intelLines, setIntelLines] = useState([]);
  const [totalAssets, setTotalAssets] = useState('');
  const [dateTime, setDateTime] = useState('');

  // Live clock — long-form date/time
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      setDateTime(now.toLocaleDateString(language || 'en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }) + ' · ' + now.toLocaleTimeString(language || 'en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }));
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchIntel = async () => {
      try {
        // Fetch aggregate revenue (total assets under monitoring)
        const { data: revenueData } = await supabase
          .from('companies')
          .select('revenue');

        const totalRevenue = (revenueData || []).reduce((sum, c) => sum + (c.revenue || 0), 0);
        const companyCount = revenueData?.length || 0;

        // Fetch connection counts
        const [
          { count: execCount },
          { count: investCount },
          { count: pacCount },
        ] = await Promise.all([
          supabase.from('executives').select('*', { count: 'exact', head: true }),
          supabase.from('investments').select('*', { count: 'exact', head: true }),
          supabase.from('pac_relationships').select('*', { count: 'exact', head: true }),
        ]);

        const connections = (execCount || 0) + (investCount || 0) + (pacCount || 0);

        // Format total assets
        let assetsStr = '';
        if (totalRevenue >= 1_000_000_000_000) {
          assetsStr = `$${(totalRevenue / 1_000_000_000_000).toFixed(1)}T+`;
        } else if (totalRevenue >= 1_000_000_000) {
          assetsStr = `$${(totalRevenue / 1_000_000_000).toFixed(0)}B+`;
        } else {
          assetsStr = `$${(totalRevenue / 1_000_000).toFixed(0)}M+`;
        }
        setTotalAssets(assetsStr);

        // Build rotating intel lines
        setIntelLines([
          `Monitoring ${assetsStr} in corporate revenue`,
          `Tracking ${connections.toLocaleString()}+ corporate & political connections`,
          `${companyCount} corporations under watch`,
          `${investCount || 0} investment relationships mapped`,
          `${execCount || 0} executive positions tracked`,
          `Follow the money. Know who profits.`,
        ]);
      } catch (err) {
        console.error('Intel fetch error:', err);
        setIntelLines(['Monitoring corporate transparency in real time']);
      }
    };
    fetchIntel();
  }, []);

  // Close mobile menu on navigation
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { to: '/', label: 'Home', icon: 'Home' },
    { to: '/search-results', label: 'Explore', icon: 'Search' },
    { to: '/shareholders', label: 'Networks', icon: 'Network' },
    { to: '/pac-directory', label: politicalEntityLabel, icon: 'Landmark' },
    { to: '/contribute', label: 'Run Research Agent', icon: 'Sparkles', highlight: true },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">

          {/* Left — Logo with icon */}
          <div className="flex items-center flex-shrink-0 -ml-2 sm:-ml-3 lg:-ml-4">
            <Link to="/" className="flex items-center gap-2">
              <span className="font-bold text-lg sm:text-xl text-gray-900 whitespace-nowrap">
                <span className="hidden sm:inline">Who You Makin' Rich</span>
                <span className="sm:hidden">$WYMR?</span>
              </span>
            </Link>
          </div>

          {/* Center — Desktop Nav */}
          <nav className="hidden md:flex items-center space-x-1">
            {navLinks.map((link) => (
              link.highlight ? (
                <Link
                  key={link.to}
                  to={link.to}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold text-white bg-gradient-to-r from-emerald-500 to-cyan-600 hover:from-emerald-400 hover:to-cyan-500 shadow-md hover:shadow-lg transition-all duration-300 whitespace-nowrap"
                >
                  <Icon name={link.icon} size={15} />
                  {link.label}
                </Link>
              ) : (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                    isActive(link.to)
                      ? 'bg-primary/10 text-primary'
                      : 'text-gray-600 hover:text-primary hover:bg-primary/5'
                  }`}
                >
                  <Icon name={link.icon} size={15} />
                  {link.label}
                </Link>
              )
            ))}
          </nav>

          {/* Right — Region selector + stacked date/time & intel ticker + mobile toggle */}
          <div className="flex items-center gap-3 min-w-0">
            <RegionSelector compact />

            {/* Stacked: date/time on top, LIVE ticker below — fits inside h-16.
                Revealed only at wider breakpoints so they never crowd the nav. */}
            <div className="hidden lg:flex flex-col items-end justify-center gap-1 flex-shrink-0">
              {/* Long-form date/time */}
              {dateTime && (
                <div className="flex items-center gap-1.5 text-[11px] text-gray-500 font-medium whitespace-nowrap leading-none">
                  <Icon name="Clock" size={12} className="text-gray-400" />
                  {dateTime}
                </div>
              )}

              {/* Intel ticker (only on xl+ to avoid crowding the nav) */}
              <div className="hidden xl:flex items-center min-w-0">
                <div className="flex items-center gap-2 bg-gray-900 text-gray-100 pl-2.5 pr-3.5 py-1 rounded-full text-xs font-medium border border-gray-700/50 min-w-0 overflow-hidden leading-none">
                  {/* Live pulse */}
                  <span className="relative flex h-2 w-2 flex-shrink-0">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
                  </span>
                  <span className="text-emerald-400 font-semibold mr-0.5 flex-shrink-0">LIVE</span>
                  <span className="text-gray-400 mx-0.5 flex-shrink-0">|</span>
                  <span className="max-w-[160px] xl:max-w-[260px] truncate">
                    <IntelFeed lines={intelLines} interval={4500} />
                  </span>
                </div>
              </div>
            </div>

            {/* Mobile menu toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-gray-600 hover:text-primary rounded-md hover:bg-gray-100 transition-colors"
              aria-label="Toggle menu"
            >
              <Icon name={mobileMenuOpen ? 'X' : 'Menu'} size={22} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile navigation menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white">
          {/* Mobile intel bar */}
          {intelLines.length > 0 && (
            <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-900 text-gray-200 text-xs font-medium overflow-hidden">
              <span className="relative flex h-2 w-2 flex-shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
              </span>
              <span className="text-emerald-400 font-semibold">LIVE</span>
              <span className="text-gray-500">|</span>
              <span className="truncate">
                <IntelFeed lines={intelLines} interval={4500} />
              </span>
            </div>
          )}

          <nav className="px-4 py-3 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-colors ${
                  link.highlight
                    ? 'text-white bg-gradient-to-r from-emerald-500 to-cyan-600 font-bold shadow-sm'
                    : isActive(link.to)
                    ? 'bg-primary/10 text-primary'
                    : 'text-gray-600 hover:text-primary hover:bg-gray-50'
                }`}
              >
                <Icon name={link.icon} size={18} />
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
